import React, { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Link } from "react-router-dom";
import {
  getConsent,
  setConsent,
  CONSENT_GRANTED,
  CONSENT_DENIED,
} from "../lib/consent";
import { initAnalytics, isAnalyticsConfigured } from "../lib/analytics";

/**
 * CookieConsent — bottom-pinned banner.
 * Phase 6 palette: warm white surface, navy text, 1px navy hairline top border,
 * gold hover underline on Accept.
 */
export const CookieConsent = () => {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const raf = window.requestAnimationFrame(() => {
      const existing = getConsent();
      if (existing === null) {
        setVisible(true);
      } else if (existing === CONSENT_GRANTED && isAnalyticsConfigured()) {
        initAnalytics();
      }
    });
    return () => window.cancelAnimationFrame(raf);
  }, []);

  const accept = () => {
    setConsent(CONSENT_GRANTED);
    if (isAnalyticsConfigured()) initAnalytics();
    setVisible(false);
  };

  const decline = () => {
    setConsent(CONSENT_DENIED);
    setVisible(false);
  };

  return (
    <AnimatePresence>
      {visible ? (
        <motion.aside
          data-testid="cookie-consent"
          role="region"
          aria-label="Cookie preferences"
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 16 }}
          transition={{ duration: 0.5, ease: [0.22, 0.61, 0.36, 1] }}
          className="fixed inset-x-0 bottom-0 z-[80] bg-offwhite text-navy border-t border-navy/10"
        >
          <div className="max-w-[1280px] mx-auto px-6 sm:px-10 py-6 lg:py-7 grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-10 items-start lg:items-center">
            <div className="lg:col-span-8">
              <p
                className="font-sans uppercase text-gold"
                style={{ fontSize: "10.5px", letterSpacing: "0.22em" }}
              >
                Cookies
              </p>
              <p className="mt-3 font-sans text-[14px] sm:text-[14.5px] leading-[1.7] text-navy/75 max-w-3xl">
                We use strictly necessary cookies to operate this site. With
                your permission, we also use analytics cookies to understand how
                visitors engage with our material. You may decline without
                affecting your experience. See our{" "}
                <Link
                  to="/legal/privacy"
                  className="underline decoration-gold underline-offset-4 hover:text-gold transition-colors"
                  data-testid="cookie-consent-privacy-link"
                >
                  Privacy Policy
                </Link>
                .
              </p>
            </div>

            <div className="lg:col-span-4 flex flex-wrap items-center gap-6 lg:justify-end">
              <button
                type="button"
                onClick={decline}
                data-testid="cookie-consent-decline"
                className="group inline-flex items-center gap-2 font-sans uppercase text-navy/75 hover:text-navy px-1 py-2 transition-colors"
                style={{ fontSize: "11px", letterSpacing: "0.22em" }}
              >
                <span className="relative">
                  Decline
                  <span
                    aria-hidden="true"
                    className="absolute left-0 right-0 -bottom-1 h-px bg-gold scale-x-0 origin-left group-hover:scale-x-100 transition-transform duration-300 ease-out"
                  />
                </span>
              </button>
              <button
                type="button"
                onClick={accept}
                data-testid="cookie-consent-accept"
                className="group inline-flex items-center gap-2 font-sans uppercase text-navy border border-navy hover:border-gold px-6 py-3 transition-colors"
                style={{ fontSize: "11px", letterSpacing: "0.22em" }}
              >
                <span className="relative">
                  Accept
                  <span
                    aria-hidden="true"
                    className="absolute left-0 right-0 -bottom-1 h-px bg-gold scale-x-0 origin-left group-hover:scale-x-100 transition-transform duration-300 ease-out"
                  />
                </span>
              </button>
            </div>
          </div>
        </motion.aside>
      ) : null}
    </AnimatePresence>
  );
};

export default CookieConsent;
